// src/pages/driverReports/ManualTripsReport.js
import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const WEEKS = ["week1", "week2", "week3", "week4", "week5"];

const WEEK_COLORS = {
  week1: "#e3f2fd", // light blue
  week2: "#e8f5e9", // light green
  week3: "#fffde7", // light yellow
  week4: "#fff3e0", // light orange
  week5: "#f3e5f5", // light purple
};

// full normalize
function normalizeName(name = "") {
  return name.toLowerCase().trim().replace(/\s+/g, " ");
}

// ✅ SAME KEY LOGIC AS MANUAL MAPPING UI
function normalizeKey(name = "") {
  return normalizeName(name).split(" ").slice(0, 3).join(" ");
}

function getWeekKeyFromDate(batchDateStr) {
  // batchDateStr: "2025-11-01"
  if (!batchDateStr) return null;
  const parts = batchDateStr.split("-");
  if (parts.length !== 3) return null;
  const day = Number(parts[2]);
  if (Number.isNaN(day)) return null;

  if (day <= 7) return "week1";
  if (day <= 14) return "week2";
  if (day <= 21) return "week3";
  if (day <= 28) return "week4";
  return "week5";
}

function getTripDateStr(trip, batchDate) {
  // ✅ match car sheets: pickupDate is the real trip date
  const d =
    trip?.pickupDate ||      // <— MOST IMPORTANT
    trip?.date ||
    trip?.tripDate ||
    trip?.rideDate ||
    batchDate;

  if (typeof d?.toDate === "function") {
    const js = d.toDate();
    const yyyy = js.getFullYear();
    const mm = String(js.getMonth() + 1).padStart(2, "0");
    const dd = String(js.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) return d;

  const js = new Date(d);
  if (!Number.isNaN(js.getTime())) {
    const yyyy = js.getFullYear();
    const mm = String(js.getMonth() + 1).padStart(2, "0");
    const dd = String(js.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  return batchDate;
}

// Helper: convert CSS hex (#rrggbb) to Excel ARGB
function cssHexToExcelARGB(cssHex) {
  const hex = cssHex.replace("#", "");
  return "FF" + hex.toUpperCase();
}

export default function ManualTripsReport({ clientId }) {
  const [month, setMonth] = useState("");
  const [weekFrom, setWeekFrom] = useState("week1");
  const [weekTo, setWeekTo] = useState("week5");
  const [companyFilter, setCompanyFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [weekTotals, setWeekTotals] = useState({});
  const [grandTotals, setGrandTotals] = useState({
    bank: 0,
    cash: 0,
    total: 0,
  });

  const [driversMap, setDriversMap] = useState({});      // firestoreId -> name
  const [manualMappings, setManualMappings] = useState({}); // normalized driverName -> driverId

  // Load driver master list
  useEffect(() => {
    async function loadDrivers() {
      const snap = await getDocs(collection(db, "drivers"));
      const map = {};
      snap.forEach((d) => {
        const data = d.data();
        map[d.id] = { name: data.fullName || data.name || "Unknown", company: data.company || data.assignedCarCompany || "" };
      });
      setDriversMap(map);
    }
    loadDrivers();
  }, []);

  // ✅ Load manual driver mappings from driverPlatformMappings/manualMappings
  useEffect(() => {
    async function loadMappings() {
      const ref = doc(db, "driverPlatformMappings", "manualMappings");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data() || {};
        // keep as-is; we only read keys that match normalizedName
        setManualMappings(data);
      } else {
        setManualMappings({});
      }
    }
    loadMappings();
  }, []);

  // Clear report when client changes
  useEffect(() => {
    setRows([]);
    setColumns([]);
    setWeekTotals({});
    setGrandTotals({ bank: 0, cash: 0, total: 0 });
  }, [clientId]);

  async function generateReport() {
    if (!month) {
      alert("Please select a month.");
      return;
    }
    if (!clientId) {
      alert("No client selected.");
      return;
    }

    const fromIndex = WEEKS.indexOf(weekFrom);
    const toIndex = WEEKS.indexOf(weekTo);
    if (fromIndex === -1 || toIndex === -1 || fromIndex > toIndex) {
      alert("Invalid week range.");
      return;
    }

    const selectedWeeks = WEEKS.slice(fromIndex, toIndex + 1);

    // Build date range for the month
    const [yearStr, monthStr] = month.split("-");
    const year = Number(yearStr);
    const monthNum = Number(monthStr); // 1-12
    if (!year || !monthNum) {
      alert("Invalid month format.");
      return;
    }
    const lastDay = new Date(year, monthNum, 0).getDate(); // last day of month
    const startDateStr = `${month}-01`;
    const endDateStr = `${month}-${String(lastDay).padStart(2, "0")}`;

    setLoading(true);
    try {
      // Load all batches for this client & month
      const qRef = query(
        collection(db, "tripImports"),
        where("clientId", "==", clientId),
        where("batchDate", ">=", startDateStr),
        where("batchDate", "<=", endDateStr)
      );
      const batchSnap = await getDocs(qRef);

      const driversAgg = {}; // key -> {driverId, driverName, weeks, totalBank, totalCash, total}
      // DEBUG: track dropped trips (excluded by week range)
let droppedCount = 0;
let droppedAmount = 0;
const droppedSamples = [];


      for (const batchDoc of batchSnap.docs) {
  const batchData = batchDoc.data();
  const batchDate = batchData.batchDate;

  // Load trips subcollection
  const tripsRef = collection(batchDoc.ref, "trips");
  const tripsSnap = await getDocs(tripsRef);

  tripsSnap.forEach((tripDoc) => {
    const trip = tripDoc.data();

    // ✅ Use trip date for week (fallback to batchDate)
    const tripDateStr = getTripDateStr(trip, batchDate);
    const weekKey = getWeekKeyFromDate(tripDateStr);
    const bankPreview = Number(trip.driverFinalPrice || 0);

if (!weekKey || !selectedWeeks.includes(weekKey)) {
  droppedCount++;
  droppedAmount += bankPreview;

  if (droppedSamples.length < 30) {
    droppedSamples.push({
      driver: trip.driver || "",
      amount: bankPreview,
      tripDateStr,
      batchDate,
      weekKey: weekKey || "INVALID",
      reason: !weekKey ? "Invalid trip date / no weekKey" : "Week not selected",
    });
  }
  
  return;
}

    const manualName = trip.driver || "";
    const normName = normalizeKey(manualName);

    const directDriverId = trip.firestoreDriverId || trip.driverId || "";

    const mappedDriverId = directDriverId || manualMappings[normName] || null;
    const firestoreDriverName = mappedDriverId
      ? driversMap[mappedDriverId]?.name || manualName
      : manualName || "Unmapped Driver";

    const aggKey = mappedDriverId || `UNMAPPED_${normName || tripDoc.id}`;

    if (!driversAgg[aggKey]) {
      driversAgg[aggKey] = {
        key: aggKey,
        driverId: mappedDriverId || "",
        driverName: firestoreDriverName,
        company: mappedDriverId ? (driversMap[mappedDriverId]?.company || "") : "",
        weeks: {},
        totalBank: 0,
        totalCash: 0,
        total: 0,
      };
    }

    const bank = Number(trip.driverFinalPrice || 0);
    const cash = 0;
    const total = bank + cash;

    const existingWeek = driversAgg[aggKey].weeks[weekKey] || { bank: 0, cash: 0 };
    existingWeek.bank += bank;
    existingWeek.cash += cash;

    driversAgg[aggKey].weeks[weekKey] = existingWeek;
    driversAgg[aggKey].totalBank += bank;
    driversAgg[aggKey].totalCash += cash;
    driversAgg[aggKey].total += total;
  });
}
console.log("❌ DROPPED trips count:", droppedCount);
console.log("❌ DROPPED amount total:", droppedAmount);
console.table(droppedSamples);

      // Dynamic columns (BANK + CASH per week)
      const cols = selectedWeeks.flatMap((w) => [
        {
          key: `${w}_bank`,
          label: `${w.toUpperCase()} BANK`,
          weekKey: w,
          type: "bank",
        },
        {
          key: `${w}_cash`,
          label: `${w.toUpperCase()} CASH`,
          weekKey: w,
          type: "cash",
        },
      ]);
      setColumns(cols);

      // Build row array
      const finalRows = Object.values(driversAgg).map((d) => {
        const row = {
          driverId: d.driverId,
          driverName: d.driverName,
          company: d.company || "",
          totalBank: d.totalBank,
          totalCash: d.totalCash,
          total: d.total,
        };
        selectedWeeks.forEach((w) => {
          const wData = d.weeks[w] || { bank: 0, cash: 0 };
          row[`${w}_bank`] = wData.bank;
          row[`${w}_cash`] = wData.cash;
        });
        return row;
      });

      setRows(finalRows);

      // Column totals
      const weekTotalsObj = {};
      selectedWeeks.forEach((w) => {
        let bankSum = 0;
        let cashSum = 0;
        finalRows.forEach((r) => {
          bankSum += r[`${w}_bank`] || 0;
          cashSum += r[`${w}_cash`] || 0;
        });
        weekTotalsObj[w] = { bank: bankSum, cash: cashSum };
      });
      setWeekTotals(weekTotalsObj);

      // Grand totals
      let gBank = 0;
      let gCash = 0;
      let gTotal = 0;
      finalRows.forEach((r) => {
        gBank += r.totalBank || 0;
        gCash += r.totalCash || 0;
        gTotal += r.total || 0;
      });
      setGrandTotals({ bank: gBank, cash: gCash, total: gTotal });
    } finally {
      setLoading(false);
    }
  }

  async function downloadExcel() {
    if (!rows.length) {
      alert("No data to export. Please generate report first.");
      return;
    }

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Manual Trips");

    const header = [
      "#",
      "Driver ID (Firestore)",
      "Driver Name",
      "Company",
      ...columns.map((c) => c.label),
      "Total Bank",
      "Total Cash",
      "Grand Total",
    ];

    ws.addRow(header);

    const headerRow = ws.getRow(1);
    headerRow.font = { bold: true };
    headerRow.alignment = { vertical: "middle", horizontal: "center" };

    headerRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };

      if (colNumber > 4 && colNumber <= 4 + columns.length) {
        const colIndex = colNumber - 5;
        const colMeta = columns[colIndex];
        const fillColor = WEEK_COLORS[colMeta.weekKey] || "#ffffff";
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: cssHexToExcelARGB(fillColor) },
        };
      } else {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFEFEFEF" },
        };
      }
    });

        const exportRows = companyFilter ? rows.filter((r) => r.company === companyFilter) : rows;

exportRows.forEach((r, idx) => {
      const rowData = [
        idx + 1,
        r.driverId || "",
        r.driverName,
        r.company || "",
        ...columns.map((c) => Number(r[c.key] || 0)),
        r.totalBank,
        r.totalCash,
        r.total,
      ];
      const excelRow = ws.addRow(rowData);

      excelRow.eachCell((cell, colNumber) => {
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
        if (colNumber > 4) {
          cell.numFmt = "0.00";
        }
      });
    });

    const totalRowValues = [
      "TOTAL",
      "",
      "",
      "",
      ...columns.map((c) => exportRows.reduce((acc, r) => acc + Number(r[c.key] || 0), 0)),
      exportRows.reduce((acc, r) => acc + Number(r.totalBank || 0), 0),
      exportRows.reduce((acc, r) => acc + Number(r.totalCash || 0), 0),
      exportRows.reduce((acc, r) => acc + Number(r.total || 0), 0),
    ];

    const totalRow = ws.addRow(totalRowValues)(totalRowValues);
    totalRow.font = { bold: true };

    totalRow.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      if (colNumber > 4) {
        cell.numFmt = "0.00";
      }
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFE0E0E0" },
      };
    });

    ws.columns.forEach((col) => {
      let maxLen = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const v = cell.value ? cell.value.toString() : "";
        if (v.length > maxLen) maxLen = v.length;
      });
      col.width = maxLen + 2;
    });

    const fileName = `manual_${clientId}_${month}_${weekFrom}-${weekTo}.xlsx`;
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), fileName);
  }

  
  const companies = Array.from(
    new Set((rows || []).map((r) => r.company).filter(Boolean))
  ).sort();

  const displayedRows = companyFilter
    ? (rows || []).filter((r) => r.company === companyFilter)
    : rows || [];
return (
    <div style={{ marginTop: 20 }}>
      <h2>Manual Trips — Driver Weekly Income Report</h2>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 20,
          marginTop: 10,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div>
          <label>Month</label>
          <br />
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
          />
        </div>

        <div>
          <label>Week From</label>
          <br />
          <select
            value={weekFrom}
            onChange={(e) => setWeekFrom(e.target.value)}
          >
            {WEEKS.map((w) => (
              <option key={w} value={w}>
                {w.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>Week To</label>
          <br />
          <select value={weekTo} onChange={(e) => setWeekTo(e.target.value)}>
            {WEEKS.map((w) => (
              <option key={w} value={w}>
                {w.toUpperCase()}
              </option>
            ))}
          </select>
        </div>

        
        <div>
          <label>Driver Company (Optional)</label>
          <br />
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            style={{ minWidth: 260 }}
          >
            <option value="">All Companies</option>
            {companies.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

<button
          onClick={generateReport}
          disabled={loading}
          style={{
            padding: "10px 16px",
            background: "#0037ff",
            color: "white",
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
            minWidth: 120,
          }}
        >
          {loading ? "Loading..." : "Generate"}
        </button>

        {displayedRows.length > 0 && (
          <button
            onClick={downloadExcel}
            style={{
              padding: "10px 16px",
              background: "green",
              color: "white",
              borderRadius: 6,
              border: "none",
              cursor: "pointer",
            }}
          >
            Download Excel
          </button>
        )}
      </div>

      {loading && (
        <p style={{ marginTop: 15 }}>⏳ Generating report, please wait…</p>
      )}

      {/* TABLE */}
      {displayedRows.length > 0 && (
        <table
          style={{
            marginTop: 20,
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 13,
            border: "1px solid #ccc",
          }}
        >
          <thead>
            <tr style={{ background: "#f3f4ff" }}>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>#</th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Driver ID (Firestore)
              </th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Driver Name
              </th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Company
              </th>
              {columns.map((c) => (
                <th
                  key={c.key}
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    background: WEEK_COLORS[c.weekKey] || "#ffffff",
                  }}
                >
                  {c.label}
                </th>
              ))}
              <th style={{ border: "1px solid #ccc", padding: 6 }}>Total Bank</th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>Total Cash</th>
              <th style={{ border: "1px solid #ccc", padding: 6 }}>
                Grand Total
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedRows.map((r, idx) => (
              <tr key={idx}>
                <td style={{ border: "1px solid #ccc", padding: 6 }}>{idx + 1}</td>
                <td style={{ border: "1px solid #ccc", padding: 6 }}>
                  {r.driverId || "-"}
                </td>
                <td style={{ border: "1px solid #ccc", padding: 6 }}>
                  {r.driverName}
                </td>
                <td style={{ border: "1px solid #ccc", padding: 6 }}>
                  {r.company || ""}
                </td>
                {columns.map((c) => (
                  <td
                    key={c.key}
                    style={{
                      border: "1px solid #ccc",
                      padding: 6,
                      textAlign: "right",
                      background: WEEK_COLORS[c.weekKey] || "#ffffff",
                    }}
                  >
                    {Number(r[c.key] || 0).toFixed(2)}
                  </td>
                ))}
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "right",
                  }}
                >
                  {r.totalBank.toFixed(2)}
                </td>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "right",
                  }}
                >
                  {r.totalCash.toFixed(2)}
                </td>
                <td
                  style={{
                    border: "1px solid #ccc",
                    padding: 6,
                    textAlign: "right",
                  }}
                >
                  {r.total.toFixed(2)}
                </td>
              </tr>
            ))}

            {/* TOTAL ROW */}
            <tr
              style={{
                fontWeight: "bold",
                background: "#eef2ff",
              }}
            >
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "center",
                }}
              >
                TOTAL
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                }}
              ></td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                }}
              ></td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                }}
              ></td>
              {columns.map((c) => {
                const val = displayedRows.reduce((acc, r) => acc + Number(r[c.key] || 0), 0);
                return (
                  <td
                    key={c.key}
                    style={{
                      border: "1px solid #ccc",
                      padding: 6,
                      textAlign: "right",
                      background: WEEK_COLORS[c.weekKey] || "#ffffff",

                    }}
                  >
                    {val.toFixed(2)}
                  </td>
                );
              })}
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "right",
                }}
              >
                {displayedRows.reduce((a,r)=>a+Number(r.totalBank||0),0).toFixed(2)}
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "right",
                }}
              >
                {displayedRows.reduce((a,r)=>a+Number(r.totalCash||0),0).toFixed(2)}
              </td>
              <td
                style={{
                  border: "1px solid #ccc",
                  padding: 6,
                  textAlign: "right",
                }}
              >
                {displayedRows.reduce((a,r)=>a+Number(r.total||0),0).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
}
